import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { isValid, parseISO } from 'date-fns'
import { uniqBy } from 'lodash'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getRads } from '../../../api/rads/get-rads'
import { getTrips } from '../../../api/trips/get-trips'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { CenterCompact } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { StaffRequestInput } from '../../../lib/types/models/staff-request/staff-request-input'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import CountryFlag from '../../elements/country-flag'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import PeriodBadge from '../../elements/period-badge'
import ValidationButtons from '../../elements/validation-buttons'
import { REQUEST_STAFF_SCHEMA } from './center.utils'

type Props = BaseFormProps<StaffRequestInput, true> & {
  centers: CenterCompact[]
}

const RequestStaffForm = ({ centers, defaultValues, ...validationButtonProps }: Props) => {
  const { t } = useTranslate()

  const form = useForm<StaffRequestInput>({
    resolver: zodResolver(REQUEST_STAFF_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  // get the countries of all the centers and check if they are available in the countries array
  const centersCountries = centers?.map((center) => center.journeyPlace?.country).filter(Boolean)
  // get the first country as all the countries are the same
  const countrycode = centersCountries?.at(0)?.code

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips({ filter: countrycode ? { countryCodes: [countrycode] } : undefined })
  })

  const { data: rads } = useQuery({
    queryKey: ['rads'],
    queryFn: () => getRads()
  })

  const qualification = form.watch('qualification')
  const startDate = form.watch('startDate')
  const endDateCalendarStart = useMemo(() => {
    const parsed = startDate ? parseISO(startDate) : undefined
    return parsed && isValid(parsed) ? parsed : undefined
  }, [startDate])
  const allowedQualifications =
    centers.length === 0
      ? [StaffQualificationEnum.FAC_PARIS]
      : Object.values(StaffQualificationEnum)

  useEffect(() => {
    if (centers.length === 0 && qualification !== StaffQualificationEnum.FAC_PARIS) {
      form.setValue('qualification', StaffQualificationEnum.FAC_PARIS, { shouldValidate: true })
    }
  }, [centers.length, form, qualification])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className={cn('flex flex-col gap-3', MODAL_CONTENT_CLASSNAME)}
      >
        {centers && centers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {centers.map((center) => (
              <div key={center.id} className="flex flex-wrap items-center gap-2">
                <Badge text={center.name} />
                {uniqBy(
                  center.centerToJourneys.map(({ journey }) => ({
                    period: journey.date.period,
                    date: `${DateFormatter.DDMMYYYY(journey.date.departureDate)} - ${DateFormatter.DDMMYYYY(journey.date.returnDate)}`
                  })),
                  'date'
                ).map(({ period, date }) => {
                  if (!period) return <Badge key={date} text={date} />
                  return <PeriodBadge key={date} period={period} text={date} />
                })}
              </div>
            ))}
            {countrycode && (
              <div className={'rounded p-1 border border-grey-light'}>
                <CountryFlag code={countrycode} />
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="qualification"
            render={({ field }) => (
              <FormItem className="flex-1 ">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    dropDownPosition="above"
                    label={'Qualification'}
                    options={allowedQualifications.map((value) => ({
                      label: t(staffQualificationEnumMapping[value].label),
                      value
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label: field.value
                        ? t(staffQualificationEnumMapping[field.value].label)
                        : '',
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'numberOfStaff'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    required
                    {...field}
                    type={'number'}
                    label={'Nombre'}
                    onChange={(val) => field.onChange(Number(val))}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    mode="over"
                    label={'Date de début'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    mode="over"
                    label={'Date de fin'}
                    startDate={endDateCalendarStart}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="countryId"
            render={({ field }) => (
              <FormItem className={'flex-1'} hidden={centers.length === 0}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    dropDownPosition="above"
                    allowNewOption
                    options={countries?.data.map((country) => ({
                      label: country.name,
                      value: country.id
                    }))}
                    label={'Pays'}
                    selectedOption={conditionalRender(!!field.value, {
                      label:
                        countries?.data.find((country) => country.id === field.value)?.name ?? '',
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'formulas'}
            render={({ field }) => (
              <FormItem className={'flex-1'} hidden={centers.length === 0}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    label={'Formules'}
                    variant={'primary'}
                    dropDownPosition="above"
                    options={
                      formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((formulaId) => ({
                        label: getTranslatedName(
                          formulas?.data.find((formula) => formula.id === formulaId)
                        ),
                        value: formulaId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name={'tripId'}
          render={({ field }) => (
            <FormItem className={'flex-1'} hidden={centers.length === 0}>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  dropDownPosition="above"
                  label={'Voyage'}
                  options={trips?.data.map((trip) => ({
                    label: TripProperties.getTripLabel(trip),
                    value: trip.id
                  }))}
                  selectedOption={conditionalRender(!!field?.value, {
                    label: TripProperties.getTripLabel(
                      trips?.data.find((trip) => trip.id === field.value)
                    ),
                    value: field.value
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {qualification && staffQualificationEnumMapping[qualification]?.type === 'french' && (
          <FormField
            control={form.control}
            name={'radId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    dropDownPosition="above"
                    label={'Date de réunion avant départ'}
                    options={rads?.data.map((rad) => ({
                      label: CenterProperties.getRadNameForRequestStaff(rad),
                      value: rad.id
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label: CenterProperties.getRadNameForRequestStaff(
                        rads?.data.find((rad) => rad.id === field.value)
                      ),
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default RequestStaffForm
